/**
 * SubmitReview Page
 *
 * Standalone form for users to submit anonymous neighborhood reviews.
 * These reviews build the RAG corpus for qualitative preference matching
 * (Sunday 2026-04-26 will wire retrieval into AnalysisAgent).
 *
 * Route: /submit-review
 * Optional URL pre-fill: /submit-review?state=TX&city=Austin&neighborhood=Mueller
 *   (deep-link from a card or external referrer pre-fills location fields)
 *
 * Created 2026-04-25 as part of Week 2 RAG build.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { submitReview } from '../services/reviewsApi';
import LogoIcon from '../components/Brand/LogoIcon';
import './SubmitReview.css';

// 50 US states + DC (2-letter codes). Shipped as a static const to avoid
// pulling in another dependency just for state lists.
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY'
];

const REVIEWER_TYPES = [
  { value: 'resident',  label: 'Resident',  description: 'I live (or lived) there' },
  { value: 'visitor',   label: 'Visitor',   description: "I've spent time there but didn't live there" },
  { value: 'realtor',   label: 'Realtor',   description: 'I work in real estate / know the area professionally' },
  { value: 'anonymous', label: 'Anonymous', description: "I'd rather not say" }
];

const MIN_REVIEW_LEN = 20;
const MAX_REVIEW_LEN = 5000;

const SubmitReview = () => {
  const [searchParams] = useSearchParams();
  // Round 4 (LG feedback): Cancel goes back to wherever the user came
  // from (chat or splash), not a hardcoded route. Falls back to / if
  // there's no history (e.g. someone deep-links into /submit-review).
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const [form, setForm] = useState({
    state:            searchParams.get('state')        || '',
    city:             searchParams.get('city')         || '',
    neighborhoodName: searchParams.get('neighborhood') || '',
    reviewText:       '',
    reviewerType:     'anonymous',
    rating:           null
  });

  const [submitting, setSubmitting]   = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);  // { id, embedded, message }
  const [errorMsg, setErrorMsg]       = useState(null);

  // If URL params change (e.g., user clicks another deep-link), refresh prefills
  useEffect(() => {
    const stateParam = searchParams.get('state');
    const cityParam  = searchParams.get('city');
    const nbhdParam  = searchParams.get('neighborhood');
    if (stateParam || cityParam || nbhdParam) {
      setForm(prev => ({
        ...prev,
        state:            stateParam || prev.state,
        city:             cityParam  || prev.city,
        neighborhoodName: nbhdParam  || prev.neighborhoodName
      }));
    }
  }, [searchParams]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear stale error/success on edit
    if (errorMsg) setErrorMsg(null);
    if (successInfo) setSuccessInfo(null);
  };

  const reviewLen = form.reviewText.trim().length;
  const reviewLenValid = reviewLen >= MIN_REVIEW_LEN && reviewLen <= MAX_REVIEW_LEN;
  const formValid =
    form.state &&
    form.city.trim() &&
    form.neighborhoodName.trim() &&
    reviewLenValid &&
    form.reviewerType;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid || submitting) return;

    setSubmitting(true);
    setErrorMsg(null);
    setSuccessInfo(null);

    const payload = {
      state:            form.state,
      city:             form.city.trim(),
      neighborhoodName: form.neighborhoodName.trim(),
      reviewText:       form.reviewText.trim(),
      reviewerType:     form.reviewerType
    };
    if (form.rating != null) payload.rating = form.rating;

    const result = await submitReview(payload);

    if (result.success && result.data?.ok) {
      setSuccessInfo({
        id:       result.data.id,
        embedded: result.data.embedded,
        message:  result.data.message
      });
      // Reset content fields, keep location for the common case of submitting
      // multiple reviews about the same neighborhood
      setForm(prev => ({
        ...prev,
        reviewText:   '',
        reviewerType: 'anonymous',
        rating:       null
      }));
    } else {
      setErrorMsg(result.error || 'Submission failed. Please try again.');
    }

    setSubmitting(false);
  };

  const renderStars = () => {
    return [1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        className={`star-btn ${form.rating != null && n <= form.rating ? 'star-filled' : ''}`}
        onClick={() => updateField('rating', form.rating === n ? null : n)}
        aria-label={`${n} star${n === 1 ? '' : 's'}`}
      >
        ★
      </button>
    ));
  };

  return (
    <div className="submit-review-page">
      {/* ─── Splash-style pill nav ─────────────────────────────────────
          Sunday polish 2026-05-03 (round 2): replaced the legacy
          centered header with a pill nav matching the splash + AIChat. */}
      <header className="sr-nav-wrap">
        <nav className="sr-nav">
          <Link to="/" className="sr-logo">
            <LogoIcon size={26} />
            <span className="sr-logo-text">
              LocalGenie<span className="sr-logo-accent">.ai</span>
            </span>
          </Link>
          <Link to="/home" className="sr-nav-cta">
            Try LocalGenie →
          </Link>
        </nav>
      </header>

      <main className="sr-main">
        <form className="sr-form" onSubmit={handleSubmit} noValidate>
          {/* ─── Card header (eyebrow + headline + subtitle) ─────────── */}
          <div className="sr-card-header">
            <span className="sr-eyebrow">Share a review</span>
            <h1>
              Tell us about your{' '}
              <span className="sr-headline-accent">neighborhood.</span>
            </h1>
            <p className="sr-subtitle">
              Help future home-seekers by describing what it's actually like to
              live in or visit a place. Reviews are anonymous by default and
              help our AI match qualitative preferences.
            </p>
          </div>
          {/* ─── Location ─── */}
          <section className="sr-section">
            <h2>Where are you reviewing?</h2>

            <div className="sr-row">
              <div className="sr-field sr-field-narrow">
                <label htmlFor="state">State *</label>
                <select
                  id="state"
                  value={form.state}
                  onChange={e => updateField('state', e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {US_STATES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="sr-field">
                <label htmlFor="city">City *</label>
                <input
                  id="city"
                  type="text"
                  value={form.city}
                  onChange={e => updateField('city', e.target.value)}
                  placeholder="e.g., Austin"
                  maxLength={80}
                  required
                />
              </div>
            </div>

            <div className="sr-field">
              <label htmlFor="neighborhood">Neighborhood Name *</label>
              <input
                id="neighborhood"
                type="text"
                value={form.neighborhoodName}
                onChange={e => updateField('neighborhoodName', e.target.value)}
                placeholder="e.g., Mueller, South Congress, North Loop"
                maxLength={120}
                required
              />
            </div>
          </section>

          {/* ─── Review text ─── */}
          <section className="sr-section">
            <h2>Your review</h2>
            <div className="sr-field">
              <label htmlFor="reviewText">
                What's it like to live in or spend time in this neighborhood? *
              </label>
              <textarea
                id="reviewText"
                value={form.reviewText}
                onChange={e => updateField('reviewText', e.target.value)}
                placeholder="Describe the vibe, the people, the day-to-day. Examples: walkability, community feel, family-friendliness, nightlife, safety, character. The more specific, the more useful for future home-seekers."
                rows={8}
                maxLength={MAX_REVIEW_LEN}
                required
              />
              <div className="sr-char-counter">
                <span className={reviewLenValid ? 'sr-counter-ok' : 'sr-counter-warn'}>
                  {reviewLen}
                </span>
                {' / '}
                {MAX_REVIEW_LEN} characters
                {reviewLen < MIN_REVIEW_LEN && (
                  <span className="sr-counter-hint">
                    {' '}(at least {MIN_REVIEW_LEN} required)
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* ─── Reviewer info ─── */}
          <section className="sr-section">
            <h2>About you</h2>
            <p className="sr-section-hint">
              Helps us calibrate trust. Your name is never collected.
            </p>

            <div className="sr-radio-group" role="radiogroup" aria-label="Reviewer type">
              {REVIEWER_TYPES.map(rt => (
                <label
                  key={rt.value}
                  className={`sr-radio-card ${form.reviewerType === rt.value ? 'sr-radio-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="reviewerType"
                    value={rt.value}
                    checked={form.reviewerType === rt.value}
                    onChange={() => updateField('reviewerType', rt.value)}
                  />
                  <span className="sr-radio-label">
                    <strong>{rt.label}</strong>
                    <span className="sr-radio-desc">{rt.description}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className="sr-field sr-rating-field">
              <label>Optional rating</label>
              <div className="sr-stars" role="group" aria-label="Star rating">
                {renderStars()}
                {form.rating != null && (
                  <button
                    type="button"
                    className="sr-clear-rating"
                    onClick={() => updateField('rating', null)}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* ─── Submit + status ─── */}
          {errorMsg && (
            <div className="sr-message sr-error" role="alert">
              ⚠️ {errorMsg}
            </div>
          )}

          {successInfo && (
            <div className="sr-message sr-success" role="status">
              <strong>Thanks for sharing!</strong>
              {' '}Your review is saved and will help future home-seekers
              understand this neighborhood.
            </div>
          )}

          <div className="sr-actions">
            {/* Cancel — returns to wherever the user came from (chat or
                splash). Round 4 (LG feedback): was hardcoded to /home, now
                uses navigate(-1) so it respects history. Label kept generic
                ("Cancel") since destination depends on referrer. */}
            <button
              type="button"
              onClick={goBack}
              className="sr-cancel-link"
            >
              ← Cancel
            </button>
            <button
              type="submit"
              className="sr-submit-btn"
              disabled={!formValid || submitting}
            >
              {submitting ? 'Submitting…' : 'Submit review'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default SubmitReview;
